import React from "react";
import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <article className="policy-page">
      <span className="eyebrow">Brisbane preview · Data handling</span>
      <h1>Your research, your information.</h1>
      <p>
        This page describes how the current preview uses information. The public
        privacy policy is still being finalised.
      </p>
      <h2>Who operates Prop AI</h2>
      <p>
        Prop AI is operated by R2 Real Estate Co PTY LTD, ABN 12 695 647 762.
        The director is Uttorio Datta. For privacy questions, access, correction
        or account-deletion requests, email{" "}
        <a href="mailto:enquiry@r2realestate.com.au">
          enquiry@r2realestate.com.au
        </a>
        .
      </p>
      <h2>Research and financial inputs</h2>
      <p>
        Your address, property details and entered price are sent to the Prop AI
        service to prepare a report. Searching council addresses sends the
        search text to Brisbane City Council. Nearby-record checks send
        geographic queries to council after validating the selected address.
        Financial calculations use the assumptions you submit to Prop AI. They
        are not sent to referral partners.
      </p>
      <p>
        Research drafts remain in the current app session. An address and price
        opened through a research link may appear in the URL and browser
        history. Do not share a research URL if you do not want its contents
        shared.
      </p>
      <h2>Accounts, saved reports and conversation workspaces</h2>
      <p>
        Creating an account provides your name, email and password to Prop AI.
        Passwords are hashed. Sign-in tokens are stored in this browser so you
        can access your account. Signing out clears these tokens.
      </p>
      <p>
        If you choose Google sign-in, Google provides Prop AI with a verified
        account identifier, email address and profile name. Prop AI verifies the
        sign-in token with Google and does not receive your Google password.
      </p>
      <p>
        Saving a report stores its inputs and evidence in your account.
        Completed financial scenarios and loaded locality snapshots are included
        only when you select their respective save options. You can delete saved
        reports from <Link to="/saved">Saved &amp; compare</Link>. Deleting a
        saved report does not delete your account or necessarily remove copies
        from backups.
      </p>
      <h2>Maps and external sources</h2>
      <p>
        The boundary file is served by Prop AI. Street-map tiles load from
        OpenStreetMap when a matched parcel is displayed or you choose to load
        them. That provider receives ordinary connection information, including
        your IP address, and requests identifying the displayed map area. Source
        links open external websites governed by their own practices.
      </p>
      <h2>Access, correction and deletion</h2>
      <p>
        Email from the address associated with your account and describe your
        request. We may need to verify your identity before releasing
        information or deleting an account. Do not email your password. Privacy
        concerns and complaints can also be sent to the same address for review
        and response.
      </p>
      <h2>Retention and backups</h2>
      <p>
        Free accounts, saved reports and saved conversation workspaces are
        intended to be deleted after 90 days of inactivity. The activity tracking and automated deletion
        process are still being implemented; automatic expiry is not active in
        this preview. You can request deletion by email in the meantime.
      </p>
      <p>
        The intended maximum backup retention is 30 days. Backup expiry and the
        process for keeping deleted data out of restored accounts must be
        verified before public launch. Deleting a live record does not
        immediately erase existing backup copies.
      </p>
      <h2>Infrastructure and service providers</h2>
      <p>
        The deployment setup specifies Cloudflare for the website and DNS, AWS
        Lightsail in Sydney for the application, and MongoDB Atlas in Sydney for
        account, saved-report and saved-workspace storage. Live provider settings, logging,
        backups and any overseas processing remain subject to launch
        verification. Sydney hosting does not mean every provider processes all
        information only in Australia.
      </p>
      <p>
        The standard research report uses source data, calculations and
        templated explanations without contacting an AI model. If you choose
        to use “Ask Prop AI”, your question and a limited set of report metrics
        are sent to OpenAI to prepare an answer. Prop AI excludes the street
        address and internal source payloads from that request. OpenAI may
        process this information outside Australia; do not include personal or
        confidential information in your question. No analytics or advertising
        tracker is configured in the reviewed preview.
      </p>
      <p>
        General Ask Prop AI chats remain temporary unless you are signed in and
        explicitly save a conversation workspace. Saved workspaces are stored
        with your account so you can reopen or continue them, and can be deleted
        from the workspace list.
      </p>
      <h2>R2 and other businesses</h2>
      <p>
        R2 Real Estate Co PTY LTD handles information as the operator of Prop
        AI. Prop AI currently does not pass account details, reports or
        financial scenarios into real-estate sales or referral workflows, or
        share them with other partners. Service-provider processing described
        above supports operation of the service.
      </p>
      <p>
        Visiting the R2 website is a separate interaction. The link does not
        include your account details or report. Any future sharing feature will
        be explained before you choose to use it. Read{" "}
        <Link to="/independence">our relationship disclosure</Link>.
      </p>
      <h2>Before the public beta</h2>
      <p>
        Retention enforcement, the email request-handling process, provider
        locations, operational logs and backup deletion must be verified before
        this notice becomes a complete public privacy policy.
      </p>
    </article>
  );
}
